import { useNavigate, useParams } from '@tanstack/react-router';
import { memo, useCallback, useMemo } from 'react';
import { MenuTrigger, SubmenuTrigger } from 'react-aria-components';

import { Button, Heading, Menu, MenuItem, Popover, Separator, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { LLM_MODELS } from '@/components/flow';
import { ChevronIcon, HelpIcon, RerunIcon, SettingsIcon } from '@/components/icons';
import { DEFAULT_SELECTED_NODE } from '@/constants';
import { RerunAgentRunsModal, useRerunCallbacks } from '@/features/agents';
import { toAgentId, toModelId, useGetAgent, useGetModel } from '@/hooks/api';
import { findModelId } from '@/utils';

export type AIModelMenuProps = object;

// eslint-disable-next-line no-empty-pattern
const IAIModelMenu = ({}: AIModelMenuProps = {}) => {
  const { agentId, runId } = useParams({ strict: false });
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const modelId = useMemo(() => findModelId(agent), [agent]);
  const { data: model } = useGetModel(toModelId(modelId));

  const navigate = useNavigate();

  const goToModelSettings = useCallback(async () => {
    if (!agent) return;
    if (modelId) sessionStorage.setItem(DEFAULT_SELECTED_NODE, modelId);
    return navigate({ to: '/agents/$agentId/integration', params: { agentId: agent.agentId } });
  }, [agent, modelId, navigate]);

  const { onRerunFromStart, onRerunFromExport, progress, isPending } = useRerunCallbacks({
    agentId,
    selectedRunIds: runId ? new Set([runId]) : undefined,
  });

  const currentLLMModel = useMemo(() => {
    return LLM_MODELS.find((llmModel) => llmModel.id === model?.llmVersion);
  }, [model?.llmVersion]);

  return (
    <>
      <RerunAgentRunsModal progress={progress} numRuns={runId ? 1 : 0} isOpen={isPending} />
      <MenuTrigger>
        <TooltipTrigger>
          <Button excludeFromTabOrder variant="secondary" className="w-fit px-1 py-0.5">
            {currentLLMModel?.icon}
            <Text>{currentLLMModel?.name}</Text>
            <ChevronIcon rotation="down" />
          </Button>

          <Tooltip className="flex flex-col" variant="primary">
            <Text>AI Model</Text>
            <Text className="!text-gray-400">Click to edit</Text>
          </Tooltip>
        </TooltipTrigger>

        <Popover className="flex flex-col gap-0 p-0 shadow-md">
          <div className="gap-sm flex items-center rounded-t-lg px-2 pt-2 pb-1">
            <Heading className="text-gray-600" size="xs">
              AI settings
            </Heading>
          </div>

          <Menu>
            <SubmenuTrigger>
              <MenuItem>
                <RerunIcon />
                <Text>Rerun</Text>
                <div className="grow" />
                <ChevronIcon rotation="right" />
              </MenuItem>

              <Popover className="shadow-md" offset={-4}>
                <Menu>
                  <MenuItem onAction={onRerunFromStart}>
                    <Text>Rerun from start</Text>
                  </MenuItem>
                  <MenuItem onAction={onRerunFromExport}>
                    <Text>Rerun export</Text>
                  </MenuItem>
                </Menu>
              </Popover>
            </SubmenuTrigger>

            <MenuItem onAction={goToModelSettings}>
              <SettingsIcon />
              <Text>Agent settings</Text>
            </MenuItem>

            <Separator orientation="horizontal" />

            <MenuItem onAction={() => window.open('https://docs.cradl.ai/core-concepts/ai-model/overview', '_blank')}>
              <HelpIcon />
              <Text className="!text-gray-500">Learn about AI models</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
    </>
  );
};

export const AIModelMenu = memo(IAIModelMenu) as typeof IAIModelMenu;
